import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import DashboardHeader from "@/components/DashboardHeader";
import DashboardFooter from "@/components/DashboardFooter";
import { useOptionalMerchant } from "@/lib/useMerchant";
import BackToDashboard from "@/components/BackToDashboard";

/**
 * Plain-English reference for every metric on the Analytics page (Growth
 * and Pro tiers). Companion to StatusGuide.jsx and TabsGuide.jsx, which
 * cover status badges and dashboard tabs respectively — this one covers
 * Analytics specifically, since several of its metrics (Chats, Returns
 * filed, Revenue Forecast, Customer Segments) mean something narrower or
 * different than they sound, and getting that wrong could lead to a real
 * business decision off a misread number.
 */

const proPill =
  "ml-2 inline-flex rounded-full bg-gradient-to-r from-hotpink via-red-500 to-orange-500 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em] text-white align-middle";

function MetricRow({ name, pro, meaning }) {
  return (
    <div className="flex flex-col gap-1.5 border-b border-ink/5 py-4 last:border-b-0 sm:flex-row sm:items-start sm:gap-6">
      <div className="sm:w-64 sm:flex-none">
        <span className="text-sm font-bold text-ink">
          {name}
          {pro && <span className={proPill}>Pro</span>}
        </span>
      </div>
      <p className="text-sm text-ink/70">{meaning}</p>
    </div>
  );
}

function Section({ title, subtitle, rows }) {
  return (
    <div className="rounded-[2rem] border border-ink/10 bg-white p-6 md:p-8">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">{title}</p>
      <p className="mt-1 text-sm text-ink/60">{subtitle}</p>
      <div className="mt-5">
        {rows.map((r) => (
          <MetricRow key={r.name} {...r} />
        ))}
      </div>
    </div>
  );
}

const CORE_METRICS = [
  {
    name: "Revenue",
    meaning: "Sum of paid orders (Paid or Paid — Manual Review) in the selected range.",
  },
  {
    name: "Chats",
    meaning:
      "Conversations with any activity in the selected range — not just ones that started in it. A customer who first messaged weeks ago and comes back to order today still counts here, since their conversation is still active.",
  },
  {
    name: "Paid orders",
    meaning: "Count of orders in the selected range with status Paid or Paid — Manual Review.",
  },
  {
    name: "Avg. order value",
    meaning: "Revenue ÷ Paid orders, for the selected range.",
  },
  {
    name: "Conversion",
    meaning:
      "Paid orders ÷ Chats. Shows “—” instead of “0%” when there are zero active chats in range, since a true 0% would wrongly imply nothing converted.",
  },
  {
    name: "Returns filed",
    meaning:
      "Count of return requests filed in the selected range — not necessarily for the same orders as the Paid Orders count next to it. A return this week can be for an order placed weeks ago, so the “% of paid orders” figure can legitimately go over 100%.",
  },
  {
    name: "Trend arrows (± % within range)",
    meaning:
      "Compares the second half of the selected range to the first half of the same range — a free signal computed from data already on the page, not a true previous-period comparison. Read it as “is this accelerating or slowing down within this window,” not “vs last month.”",
  },
];

const CHARTS = [
  {
    name: "Revenue over time",
    meaning: "Daily revenue for the range, paid orders only. Hover any point for the exact date and amount.",
  },
  {
    name: "Best day",
    meaning: "The single highest-revenue day in the selected range, marked on the chart.",
  },
  {
    name: "DM engagement",
    meaning: "Same Chats definition as the KPI card above, shown day by day.",
  },
  {
    name: "Orders per day",
    meaning: "Paid orders, day by day.",
  },
  {
    name: "Revenue by day of week",
    meaning: "Total revenue for each weekday (Sun–Sat) across the whole selected range — which days actually convert.",
  },
];

const PRODUCTS = [
  {
    name: "Top products",
    meaning: "Best sellers by revenue, paid orders only, top 8 in the selected range.",
  },
  {
    name: "Dead stock",
    meaning:
      "Catalog items with the fewest (or zero) units sold in the selected range. We don't track product views, so this is the honest stand-in for “lowest converting product” — it tells you what isn't selling, not why.",
  },
];

const AUTOMATION = [
  {
    name: "Auto-resolved conversations",
    meaning:
      "Conversations that reached a fully completed state (payment confirmed, or a return handled) without ever landing in manual review, in the selected range. This is a real, measured count.",
  },
  {
    name: "Estimated hours / staff cost saved",
    meaning:
      "A calculator, not a measurement — it multiplies the real Auto-resolved count above by two numbers you set yourself (minutes per manual reply, hourly staff cost). Change the assumptions to match your team; they're saved in your browser only.",
  },
];

const PRO_FUNNEL = [
  {
    name: "DM → Paid funnel",
    pro: true,
    meaning:
      "Three stages: DMs received (same as Chats), Checkout started (an order record was created — the closest available proxy for “reached checkout,” since cart-add events aren't logged historically), and Paid.",
  },
  {
    name: "Order status breakdown",
    pro: true,
    meaning: "Every order created in the selected range, grouped by its current status.",
  },
  {
    name: "Avg. DM → checkout",
    pro: true,
    meaning: "Average time between a customer's first message and an order being created for them.",
  },
  {
    name: "Avg. return resolution",
    pro: true,
    meaning: "Average time between a return being requested and you approving or rejecting it.",
  },
  {
    name: "Cart abandonment",
    pro: true,
    meaning: "% of conversations in range with items sitting in the cart that never reached a finished state.",
  },
  {
    name: "When DMs come in",
    pro: true,
    meaning: "What hour of the day (0–23) chats most often start — useful for knowing when to be online.",
  },
];

const PRO_FORECAST_AND_CUSTOMERS = [
  {
    name: "Revenue forecast",
    pro: true,
    meaning:
      "A simple run-rate for the current calendar month: (month-to-date revenue ÷ days elapsed) × days in the month. It's today's pace held steady, not a predictive model — and unlike the rest of the page, it's always based on the current month, not whatever range you have selected.",
  },
  {
    name: "Customer segments (VIP / Loyal / At Risk / New / Lost)",
    pro: true,
    meaning:
      "Based on a customer's entire order history, not just the selected range — this is intentional, since it wouldn't make sense to call someone “Lost” using only a 7-day window. Refreshed once a day, not live.",
  },
  {
    name: "→ VIP",
    pro: true,
    meaning: "3+ orders, and total spend above the average paying customer.",
  },
  {
    name: "→ Loyal",
    pro: true,
    meaning: "2+ orders, most recent one within the last 60 days.",
  },
  {
    name: "→ At Risk",
    pro: true,
    meaning: "2+ orders, but nothing in the last 60+ days.",
  },
  {
    name: "→ Lost",
    pro: true,
    meaning: "Nothing ordered in 120+ days.",
  },
  {
    name: "→ New",
    pro: true,
    meaning: "Exactly one order, placed in the last 30 days.",
  },
  {
    name: "→ Regular",
    pro: true,
    meaning: "Everyone else — a single order, more than 30 days ago.",
  },
  {
    name: "Badge priority",
    pro: true,
    meaning:
      "A customer gets exactly one badge. Order of priority: Lost → At Risk → VIP → Loyal → New → Regular. A big spender who's gone quiet shows as Lost or At Risk, not VIP — that's deliberate, since a valuable customer going quiet is the more actionable thing to see.",
  },
  {
    name: "Top spenders",
    pro: true,
    meaning:
      "Your 5 highest all-time spenders by total paid order value, regardless of badge. Unlike the VIP list, this ignores order count and recency — a customer who placed one large order can appear here even if they're badged New, Lost, or Regular.",
  },
];

export default function AnalyticsGuide() {
  const { me } = useOptionalMerchant();

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    headline: "Analytics Guide",
    description: "What every metric on the Checkout DM Analytics page means, including a few that mean something narrower than they sound.",
    publisher: { "@type": "Organization", name: "Checkout DM", url: "https://checkoutdm.com" },
    mainEntityOfPage: "https://checkoutdm.com/analytics-guide",
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://checkoutdm.com/" },
      { "@type": "ListItem", position: 2, name: "Analytics Guide", item: "https://checkoutdm.com/analytics-guide" },
    ],
  };

  return (
    <div className="min-h-screen bg-cream">
      <SEO
        title="Analytics Guide | Checkout DM"
        description="What every metric on the Checkout DM Analytics page means, including a few that mean something narrower than they sound."
        path="/analytics-guide"
        structuredData={[articleSchema, breadcrumbSchema]}
      />
      <DashboardHeader me={me} />

      <main className="mx-auto max-w-4xl px-6 pt-10 md:px-10">
        {me && <BackToDashboard />}
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Help</p>
        <h1 className="mt-2 font-display text-4xl font-black tracking-tighter">
          What every Analytics number means
        </h1>
        <p className="mt-3 max-w-2xl text-ink/70">
          A few of these mean something narrower than they sound — worth a quick read before you
          act on a number here.{" "}
          <Link to="/status-guide" className="font-semibold text-hotpink hover:underline">
            Looking for what a status badge means instead? →
          </Link>
        </p>

        <div className="mt-8 space-y-6">
          <Section
            title="Core KPIs"
            subtitle="The top row, and the two big charts under it — available on Growth and Pro."
            rows={CORE_METRICS}
          />

          <Section
            title="Charts & trends"
            subtitle="All follow the range picker (7/30/90 days) unless noted otherwise."
            rows={CHARTS}
          />

          <Section
            title="Product insights"
            subtitle="Which products are working, and which aren't."
            rows={PRODUCTS}
          />

          <Section
            title="Automation performance"
            subtitle="One real measurement, plus a calculator built on top of it."
            rows={AUTOMATION}
          />

          <Section
            title="Funnel & operations"
            subtitle="Pro plan only — where DMs stop converting, and how fast things move."
            rows={PRO_FUNNEL}
          />

          <Section
            title="Forecast & customer segments"
            subtitle="Pro plan only — and the two things on this page that intentionally ignore the range picker."
            rows={PRO_FORECAST_AND_CUSTOMERS}
          />

          <div className="rounded-[2rem] border border-ink/10 bg-white p-6 md:p-8">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Refresh & caching</p>
            <p className="mt-1 text-sm text-ink/60">Why the numbers don't always update the instant something changes.</p>
            <div className="mt-5 space-y-3 text-sm text-ink/70">
              <p>
                Everything on this page is cached in your browser for up to <strong>2 hours per
                range</strong> — reopening the page or switching ranges you've already viewed
                won't re-query anything until that cache expires. Hit <strong>Refresh</strong> any
                time to force a fresh pull.
              </p>
              <p>
                Customer Segments (VIP/Loyal/At Risk/New/Lost) and the repeat-customer rate are
                refreshed on their own <strong>24-hour</strong> schedule regardless of the button
                above, since they're computed from your entire order history rather than just the
                selected range.
              </p>
            </div>
          </div>
        </div>
      </main>

      <div className="mt-10">
        <DashboardFooter />
      </div>
    </div>
  );
}
